import {
  palettes,
  PaletteOptions,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';

/**
 * Defines the color palette options for the light theme in the Backstage application.
 *
 * This configuration extends the default `palettes.light` from `@dewise-devex/backstage-plugin-mui-uikit-react`
 * and customizes colors for various UI elements such as `primary`, `secondary`,
 * `background`, and `navigation`.
 *
 * @category Theming
 */
export const lightPalette: PaletteOptions = {
  ...palettes.light,

  /** Primary color used for key UI components and actions. */
  primary: {
    main: '#6E25F6',
    container: '#712AF9',
    onContainer: '#FFFFFF',
    onColor: '#FFFFFF',
    onColorFixed: '#22005D',
    colorFixed: '#E9DDFF',
    onColorFixedDim: '#CFBCFF',
    onColorFixedVar: '#5400CC',
  },

  /** Secondary color used for accents and less prominent actions. */
  secondary: {
    main: '#006879',
    container: '#28C4E0',
    onColorFixed: '#001F26',
    colorFixed: '#A8EDFF',
  },

  /** terinary color used for miscellaneous and minor prominent actions. */
  tertiary: {
    container: '#FFB748',
    onColorFixed: '#291800',
    colorFixed: '#FFDDB3',
  },

  /** neautral colors */
  neutral: {
    neutral80: '#C9C5C8',
  },

  /** extended colors */
  extended: {
    green: {
      seed: '#76CA00',
    },
  },

  /** Color used to indicate errors in the application. */
  error: {
    main: '#E91E63',
    onColor: '#FFFFFF',
    error60: '#FF4E7C',
    error70: '#FF869D',
    error80: '#FFB2BE',
    error90: '#FFD9DE',
  },

  /** Color used to indicate warnings in the application. */
  warning: {
    main: '#ff8f00',
  },

  /** Color used to convey informational messages. */
  info: {
    main: '#0091ea',
  },

  /** Color used to indicate successful actions or states. */
  success: {
    main: '#6E25F6',
  },

  /** Background colors for the UI. */
  background: {
    default: '#F7F2F6',
    paper: '#F1ECF1',
    onBackground: '#1D1A25',
  },

  /** Colors for banners displayed in the application. */
  banner: {
    info: '#0091ea',
    error: '#E91E63',
    text: '#0F0D13',
    link: '#2962ff', // Fixed to a single hash for the link color.
  },

  /** Background color for error messages or elements. */
  errorBackground: '#00B8D4',

  /** Background color for warning messages or elements. */
  warningBackground: '#8f5e15',

  /** Background color for informational messages or elements. */
  infoBackground: '#212121',

  /** Navigation-related colors. */
  navigation: {
    background: '#ddd9dd',
    indicator: '#8f5e15',
    color: '#48464E',
    selectedColor: '#ffffff',
  },

  /** Surface-related colors, defining the base layer and variations. */
  surface: {
    surface: '#FCF8FC',
    dim: '#DDD9DD',
    bright: '#FCF8FC',
    onSurface: '#0F0D13',
    onSurfaceVar: '#48464E',
    outline: '#78767F',
    outlineVariant: '#C9C5CF',
  },

  /** Inverse-related colors */
  inverse: {
    surface: '#313033',
    onSurface: '#F4EFF3',
    primary: '#CFBCFF',
  },

  /** Container surface colors for different elevation levels. */
  surfaceContainer: {
    container: '#F1ECF1',
    high: '#EBE7EB',
    highest: '#E5E1E5',
    low: '#F7F2F6',
    lowest: '#FFFFFF',
  },

  /** Hover effect color for surface elements. */
  surfaceHover: {
    onSurfaceVar: '#EEEBEE',
  },

  /** Status colors representing various system states. */
  status: {
    ok: '#1ee938',
    error: '#E91E63',
    running: '#D0CDD1',
    pending: '#ff8f00',
    aborted: '#8c4351',
    warning: '#ffca28',
  },
} as PaletteOptions;
